/**
 * THE WORLD GENERATION CONFIG — one tree, shaped by the catalog.
 *
 * See docs/node-graph-refactor-plan.md §2.6. It replaces the three flat structs that are threaded
 * through `createWorld` -> `WorldMeta` -> the worker pool -> the generator today, and the signature
 * that grows one argument per subsystem with them. C3 is what makes the generator read it; this
 * module ships first so the editor can be built against it in the same wave.
 *
 * NO MIGRATION FROM THE OLD SHAPE, deliberately (plan decision 1): C0 changed generation and C3
 * changes the config, so a stored world references a generator that no longer exists. They are
 * dropped on upgrade behind a notice, which removes the entire migration surface — no cave-config
 * normaliser, no back-compat clauses, no localStorage key ladder.
 *
 * `version` is here from day one anyway, because that is the point at which a FUTURE change gets a
 * cheap migration instead of another deletion. And `parse` is deliberately tolerant in both
 * directions: an unknown kind is dropped with a note, a missing one takes its catalog defaults. That
 * alone makes a config forward- and backward-compatible for everything after this.
 */
import { CATALOG, descriptor } from './catalog.js';
import { paramValid } from './params.js';
/**
 * 2: `base` and `flat` left (the landform tickbox IS the height model), `scatter` merged into
 * `meadow`, `wild` arrived, and `flatHeight`/`landformSeaLevel` became one `world.groundLevel`.
 * `parse` is tolerant enough to READ a v1 config, but it would read a flat world back as a landform
 * one — `landform.enabled` defaults true and the enum that said otherwise no longer exists. So a v1
 * world is dropped rather than silently reinterpreted; see WorldManager.listWorlds.
 */
export const GEN_CONFIG_VERSION = 2;
/** The config a fresh world starts from — every kind at its declared defaults. */
export function defaultGenConfig(seed) {
    const nodes = {};
    for (const d of CATALOG) {
        const params = {};
        for (const p of d.params)
            params[p.key] = p.def;
        nodes[d.kind] = { enabled: d.toggle ? d.toggle.def : true, params };
    }
    return { version: GEN_CONFIG_VERSION, seed, nodes };
}
/** Read one parameter, falling back to its declared default. */
export function getParam(cfg, kind, key) {
    const stored = cfg.nodes[kind]?.params[key];
    if (stored !== undefined)
        return stored;
    return descriptor(kind)?.params.find((p) => p.key === key)?.def;
}
export function getNumber(cfg, kind, key) {
    const v = getParam(cfg, kind, key);
    return typeof v === 'number' ? v : 0;
}
/** Is this kind on, AND is every ancestor of it on? A child of a disabled parent is inert. */
export function isEnabled(cfg, kind) {
    let k = kind;
    const seen = new Set();
    while (k && !seen.has(k)) {
        seen.add(k);
        if (cfg.nodes[k] && cfg.nodes[k].enabled === false)
            return false;
        k = parentOf(k);
    }
    return true;
}
function parentOf(kind) {
    for (const d of CATALOG)
        if (d.children.includes(kind))
            return d.kind;
    return undefined;
}
/** Set one parameter, returning a new config (the editor holds this in React state). */
export function setParam(cfg, kind, key, value) {
    const node = cfg.nodes[kind] ?? { enabled: true, params: {} };
    return {
        ...cfg,
        nodes: { ...cfg.nodes, [kind]: { ...node, params: { ...node.params, [key]: value } } },
    };
}
export function setEnabled(cfg, kind, enabled) {
    const node = cfg.nodes[kind] ?? { enabled: true, params: {} };
    return { ...cfg, nodes: { ...cfg.nodes, [kind]: { ...node, enabled } } };
}
/**
 * Read a stored config back. Unknown kinds and unknown or ill-typed parameters are dropped with a
 * note and the defaults stand in; nothing throws. A config that fails entirely (corrupt JSON, wrong
 * shape) is the caller's problem to catch — it gets `defaultGenConfig` back.
 */
export function parseGenConfig(raw, fallbackSeed) {
    const base = defaultGenConfig(fallbackSeed);
    if (!raw || typeof raw !== 'object')
        return base;
    const src = raw;
    if (typeof src.seed === 'number' && Number.isFinite(src.seed))
        base.seed = src.seed;
    if (!src.nodes || typeof src.nodes !== 'object')
        return base;
    for (const [kind, node] of Object.entries(src.nodes)) {
        const d = descriptor(kind);
        if (!d) {
            note(`unknown kind '${kind}' dropped`);
            continue;
        }
        if (!node || typeof node !== 'object')
            continue;
        if (typeof node.enabled === 'boolean')
            base.nodes[kind].enabled = node.enabled;
        for (const [key, value] of Object.entries(node.params ?? {})) {
            const spec = d.params.find((p) => p.key === key);
            if (!spec) {
                note(`unknown param '${kind}.${key}' dropped`);
                continue;
            }
            if (!paramValid(spec, value)) {
                note(`ill-typed param '${kind}.${key}' dropped`);
                continue;
            }
            base.nodes[kind].params[key] = value;
        }
    }
    return base;
}
function note(message) {
    // eslint-disable-next-line no-console
    console.warn(`[genConfig] ${message}`);
}
//# sourceMappingURL=genConfig.js.map